import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import SectionHeader from "../components/SectionHeader";
import { ArrowRight, ShieldCheck, Sparkles, HeartHandshake, Activity, Leaf, Award, Star, Quote, ChevronRight, MessageCircle } from "lucide-react";
import { useChatbot } from "../lib/chatbot";

// Category card images are lifestyle photos (the "need"), not product shots.
// Bundled from /public/categories/ so preview and production render identically
// (images ship with the frontend build — no DB / storage dependency).
// Photos should be ~3:2 landscape with the subject centred; the card crops to 5:3.
// The tint shows only while the photo loads.
const CATEGORY_VISUALS = {
  "neck-cervical": { img: "/categories/neck-cervical.jpg", tint: "bg-welljio-highlight" },
  "back-posture": { img: "/categories/back-posture.jpg", tint: "bg-welljio-soft" },
  "orthopedic-cushions": { img: "/categories/orthopedic-cushions.jpg", tint: "bg-welljio-tint" },
};

export default function HomePage() {
  const { setOpen: setChatbotOpen } = useChatbot();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, t, b] = await Promise.all([
          api.get("/products?featured=true"),
          api.get("/categories"),
          api.get("/testimonials"),
          api.get("/blog"),
        ]);
        setProducts(p.data);
        setCategories(c.data);
        setTestimonials(t.data);
        setPosts(b.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — Vibrant cobalt × coral with editorial typography */}
      <section className="relative welljio-mesh overflow-hidden" data-testid="hero-section">
        {/* Hero body */}
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-8 lg:pt-12 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 relative z-10 animate-fade-up">
            <span className="welljio-pill mb-8" data-testid="hero-pill">
              <span className="relative flex w-1.5 h-1.5">
                <span className="animate-pulse-soft absolute inline-flex w-full h-full rounded-full bg-welljio-coral opacity-70"></span>
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-welljio-coral"></span>
              </span>
              Better Support. Better Recovery.
            </span>

           <h1
  className="
    font-heading
    font-light
    text-rgb
    leading-[0.92]
    tracking-[-0.04em]
    text-[clamp(3.6rem,7vw,5.8rem)]
    max-w-[700px]
  "
>
  Where Recovery
  <br />

  <span className="welljio-underline italic">
    Meets
  </span>{" "}
  Everyday
  <br />

  Comfort.
</h1>
            <div className="mt-14 grid sm:grid-cols-[1fr_auto] gap-8 sm:gap-10 items-end max-w-2xl">
              <p className="text-base text-black/95 leading-relaxed max-w-md">
                Pillows, cushions and designed by experts and professionals argonomically — to gently restore how your body holds itself, every hour and every night.
              </p>
              <div className="flex items-center gap-5 shrink-0">
                <Link
                  to="/shop"
                  data-testid="hero-shop-now-button"
                  className="group welljio-btn-primary"
                >
                  Shop the Collection
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
                <Link
  to="/find-my-fit"
  data-testid="hero-explore-solutions-button"
  className="group welljio-btn-secondary"
>
  Find my fit
  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
</Link>
              </div>
            </div>
          </div>

          {/* RIGHT — product on colored halo */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-[540px] mx-auto">
              {/* Colorful halo */}
              <div
className="absolute inset-4 rounded-[3rem]"
style={{
background:
"linear-gradient(135deg,#F7F8F7 0%,#EEF2F0 55%,#E5ECE8 100%)"
}}
/>
              <div aria-hidden className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-welljio-coral/30 blur-2xl" />
              <div aria-hidden className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-welljio-accent/20 blur-2xl" />

              <img
                src="/hero-lifestyle.jpg"
                alt="Woman sleeping peacefully on a Welljio cervical memory pillow"
                className="relative w-full h-full object-cover rounded-[2.5rem] p-4 animate-float"
                style={{ filter: "drop-shadow(0 60px 50px rgba(13,46,36,0.20))" }}
              />

              {/* Coral bestseller badge */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-welljio-green text-white text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1.5 rounded-full shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-black" />
                #1 Bestseller
              </div>
            </div>
            {/* Editorial annotation */}
            <div className="mt-6 flex items-baseline justify-center gap-4 text-[10px] uppercase tracking-[0.32em] text-black/80">
              <span>Featured</span>
              <span className="text-welljio-coral">—</span>
              <Link
  to="/product/cervical-pillow"
  data-testid="hero-featured-product-link"
  className="text-rgbfont-semibold hover:text-black transition-colors"
>
  Cervical Memory Pillow
</Link>
            </div>
          </div>
        </div>

        {/* Bottom hairline marquee */}
<div className="border-t border-gray-200 relative z-10 bg-white">
  <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-6 text-[11px] uppercase tracking-[0.28em] text-black font-medium overflow-x-auto whitespace-nowrap">

    <span className="flex items-center gap-2">
      <Star
        className="w-3 h-3 fill-welljio-gold text-welljio-gold shrink-0"
        strokeWidth={1}
      />
      <span className="font-semibold">4.8</span>
      <span className="text-welljio-coral">·</span>
      10,000+ Recoveries
    </span>

    <span className="hidden md:inline text-welljio-coral">/</span>
    <span className="hidden md:inline">OEKO-TEX Certified</span>

    <span className="hidden md:inline text-welljio-coral">/</span>
    <span className="hidden lg:inline">Ortho Advised</span>

    <span className="hidden lg:inline text-welljio-coral">/</span>
    <span className="hidden md:inline">3-Year Warranty</span>

    <span className="hidden md:inline text-welljio-coral">/</span>
    <span className="font-semibold">Free Shipping ₹1,499+</span>

  </div>
</div>
      </section>

      {/* AI SMART ASSISTANCE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="ai-section">
        <div className="relative welljio-premium-card overflow-hidden p-10 lg:p-14">
          <div aria-hidden className="absolute inset-0 welljio-mesh" />
          <div aria-hidden className="absolute inset-0 welljio-dots opacity-50" />
          <div className="relative grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-5">
              <div className="mb-8">
  <p className="welljio-eyebrow text-black mb-5">
    Wellness, gently delivered
  </p>

  <h2 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold tracking-tight leading-[1.02] text-welljio-ink">
    A smart companion that{" "}
    <span className="text-black">listens</span> before it suggests.
  </h2>

  <p className="text-base sm:text-lg !text-black leading-relaxed mt-5 max-w-xl">
    From symptom-based recommendations to personalised support, Welljio's
    intelligent assistant guides you gently — never robotic, always supportive.
  </p>
</div>
              <button
               onClick={() => setChatbotOpen(true)}
               data-testid="ai-section-chat-cta"
               className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-6 py-3 font-medium hover:bg-welljio-accent transition-colors"
             >
               Chat with Welljio <MessageCircle className="w-4 h-4" strokeWidth={1.5}/>
             </button>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Sparkles, title: "Chat Companion", desc: "Talk to the Welljio assistant for empathetic product guidance." },
                { icon: Activity, title: "Symptom Engine", desc: "Tell us where it hurts. Get matched in seconds." },
                { icon: HeartHandshake, title: "Personalised Picks", desc: "Recommendations adapt to your routine and goals." },
                { icon: ShieldCheck, title: "FAQ Assistant", desc: "Instant answers on policies, sizing, and care." },
              ].map((f, i) => (
                <div key={f.title} className="welljio-glass-card rounded-3xl p-6 welljio-card" style={{ animationDelay: `${i*80}ms` }}>
                  <div className="w-11 h-11 rounded-2xl bg-welljio-highlight text-welljio-accent flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-1">{f.title}</h3>
                  <p className="text-sm text-welljio-muted">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="welljio-eyebrow text-welljio-ink mb-6">Shop by need</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Every category. One goal: better support.</h2>
          </div>
          <Link to="/shop" data-testid="categories-view-all" className="hidden sm:inline-flex items-center text-sm text-welljio-ink hover:underline">
            View all <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="welljio-card group bg-white border border-slate-100 rounded-3xl overflow-hidden flex flex-col transition-all duration-500"
              style={{ animationDelay: `${i*80}ms` }}
            >
              <div className={`relative aspect-[5/3] ${CATEGORY_VISUALS[c.slug]?.tint || "bg-welljio-tint"} overflow-hidden`}>
                {CATEGORY_VISUALS[c.slug]?.img && (
                  <img src={CATEGORY_VISUALS[c.slug].img} alt={c.name} className="welljio-card-image w-full h-full object-cover" />
                )}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-welljio-ink">{c.name}</h3>
                <p className="text-sm text-welljio-muted mt-1 line-clamp-2">{c.description}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm text-welljio-accent font-medium">
                  Explore
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="featured-products-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <SectionHeader
            eyebrow="The Welljio essentials"
            title=" Relief, in every form."
            className="mb-0"
          />
          <Link to="/shop" className="inline-flex items-center text-sm text-welljio-ink hover:text-welljio-accent transition-colors">
            Shop all <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p, i) => (
            <ProductCard product={p} key={p.id} index={i} />
          ))}
        </div>
      </section>

     {/* SYMPTOM CHECKER PROMO */}
<section
  className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14"
  data-testid="symptom-promo-section"
>
  <div
     className="relative welljio-card border border-[#E8ECE8] overflow-hidden rounded-[2rem] p-8 lg:p-12"
  >
    <div aria-hidden className="absolute inset-0 welljio-mesh" />
    <div aria-hidden className="absolute inset-0 welljio-dots opacity-50" />
    
  
    {/* Dot Pattern */}
    <div
      className="absolute inset-0 pointer-events-none opacity-35"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(13, 48, 38, 0.12) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        WebkitMaskImage:
          "linear-gradient(to right, rgba(0,0,0,0.9), rgba(0,0,0,0.25), transparent)",
        maskImage:
          "linear-gradient(to right, rgba(0,0,0,0.9), rgba(0,0,0,0.25), transparent)",
      }}
    />

    {/* Soft Left Glow */}
    <div
      className="absolute -left-40 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full pointer-events-none"
      style={{
        background: "rgba(15,90,70,0.22)",
        filter: "blur(120px)",
      }}
    />

    <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">

      <div>
        <p className="welljio-eyebrow text-black mb-4">
  Symptom Checker
</p>

<h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-5 text-[#0D2E24]">
  Tell us where it hurts. We'll do the rest.
</h2>

<p className="text-black leading-relaxed mb-8 max-w-md">
  A two-minute guided check matches your symptoms, pain areas and recovery goals to the right Welljio product — with an empathetic, smart explanation, not just a list.
</p>

        <Link
          to="/symptom-checker"
          data-testid="symptom-promo-cta"
          className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-6 py-3 font-medium hover:bg-welljio-accent transition-colors"
        >
          Start Symptom Check
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>

      <div className="relative">
        <img
          src="https://images.unsplash.com/photo-1754951875556-c38e7a61e51c?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80"
          alt="Posture wellness"
          className="rounded-2xl w-full object-cover aspect-[5/4]"
        />
      </div>

    </div>
  </div>
</section>
      {/* WHY CHOOSE US */}
      {/* WHY CHOOSE US */}
      <section
        className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 overflow-hidden"
        data-testid="why-choose-us-section"
      >
        {/* Green Background Glow */}
        <div
          className="absolute -left-52 top-0 w-[650px] h-[650px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(15,113,74,0.30) 0%, rgba(15,113,74,0.15) 35%, rgba(15,113,74,0.06) 60%, transparent 85%)",
            filter: "blur(110px)",
          }}
        ></div>

        <div className="relative z-10">
          <SectionHeader
            eyebrow="Why welljio"
            title="Wellness, engineered with intent."
            align="center"
          />

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Leaf, t: "Premium Materials", d: "CertiPUR memory foam, breathable bamboo blends, OEKO-TEX certified covers." },
              { icon: Activity, t: "Physio-Approved", d: "Engineered with licensed physiotherapists across India." },
              { icon: HeartHandshake, t: "Built for the Long Haul", d: "Designed for 8-hour comfort, 3+ year resilience." },
              { icon: ShieldCheck, t: "30-Day Trust", d: "Easy returns and a 1-year warranty on memory-foam products." },
              { icon: Sparkles, t: "Smart Personalisation", d: "Smart guidance that learns your routine and recovery goals." },
              { icon: Award, t: "10,000+ Happy Bodies", d: "Real recovery stories from across India." },
            ].map((b, i) => (
              <div
                key={b.t}
                className="relative rounded-[28px] border border-[#E8ECE8] transition-all duration-500 shadow-[0_10px_35px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_28px_60px_-10px_rgba(222,107,79,0.45)]"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                {/* Background layer — gradient clipped to rounded corners */}
                <div
                  className="absolute inset-0 rounded-[28px] overflow-hidden -z-10"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(15,113,74,0.22) 0%, rgba(15,113,74,0.10) 18%, rgba(255,255,255,0.96) 45%, rgba(255,255,255,1) 100%)",
                  }}
                />

                {/* Content layer */}
                <div className="relative p-8">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: "#F8E5DC",
                      color: "#0F714A",
                    }}
                  >
                    <b.icon className="w-6 h-6" strokeWidth={1.8} />
                  </div>

                  <h3
                    className="font-heading text-2xl font-semibold mb-3"
                    style={{ color: "#153C33" }}
                  >
                    {b.t}
                  </h3>

                  <p
                    className="text-base leading-7"
                    style={{ color: "#66766F" }}
                  >
                    {b.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* LIFESTYLE STRIP */}
      {/*<section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="lifestyle-section">
        <SectionHeader
          eyebrow="Everyday moments"
          title="Support that follows your day."
          align="center"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {[
            {
              img: "/lifestyle/at-the-desk.jpg",
              title: "At the desk",
              text: "Lumbar support that keeps your spine aligned through 8-hour workdays.",
              category: "back-posture",
            },
            {
              img: "/lifestyle/in-bed.jpg",
              title: "In bed",
              text: "Cervical comfort that turns restless nights into restorative sleep.",
              category: "neck-cervical",
            },
            {
              img: "/lifestyle/in-recovery.jpg",
              title: "In recovery",
              text: "Physiotherapist-grade aids for active rehabilitation.",
              category: "orthopedic-cushions",
            },
          ].map((b, i) => (
            <Link
              to={`/shop?category=${b.category}`}
              key={b.title}
              data-testid={`lifestyle-card-${b.category}`}
              className="welljio-card group relative rounded-3xl overflow-hidden border border-slate-100 bg-white block"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={b.img}
                  alt={b.title}
                  className="welljio-card-image w-full h-full object-cover"
                />*/}
                {/* Gradient overlay for legibility + hover CTA */}
                {/*<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-welljio-ink/80 backdrop-blur-sm px-3 py-1.5 rounded-full opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  Shop the fit <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold">{b.title}</h3>
                <p className="text-sm text-welljio-muted mt-1">{b.text}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>*/}

    {/* TESTIMONIALS */}
<section
  className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 overflow-hidden"
  data-testid="testimonials-section"
>
  {/* Left Green Glow */}
  <div className="absolute -left-56 top-0 w-[650px] h-[650px] rounded-full bg-gradient-to-r from-[#0f6b4d]/25 via-[#b8ddd1]/20 to-transparent blur-[120px] pointer-events-none"></div>

  <div className="relative z-10">
    <SectionHeader
      eyebrow="Stories of relief"
      title="Recovery, in their own words."
      align="center"
    />

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {testimonials.map((t, i) => (
        <div
          key={t.id}
          className="relative rounded-[30px] border border-[#e7ece8] transition-all duration-300 shadow-[0_10px_35px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_28px_60px_-16px_rgba(222,107,79,0.45)]"
          style={{
            animationDelay: `${i * 80}ms`,
            background:
              "linear-gradient(135deg,#dceee7 0%,#eef7f3 22%,#ffffff 55%,#ffffff 100%)",
          }}
        >

          {/* Background layer — gradient + card glow, clipped to rounded corners */}
         <div className="absolute inset-0 rounded-[30px] overflow-hidden -z-10"
           style={{
             background:
               "linear-gradient(135deg,#dceee7 0%,#eef7f3 22%,#ffffff 55%,#ffffff 100%)",
           }}
          >

          {/* Card Glow */}
          <div className="absolute -top-16 -left-16 w-44 h-44 rounded-full bg-gradient-to-r from-[#0f6b4d]/20 to-transparent blur-3xl"></div>
        </div>

          <div className="relative z-10 p-6">
            <Quote
              className="w-6 h-6 text-welljio-accent mb-4"
              strokeWidth={1.5}
            />

            <p className="text-sm text-welljio-ink leading-relaxed mb-6">
              "{t.story}"
            </p>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-welljio-ink">
                  {t.name}
                </p>
                <p className="text-xs text-welljio-subtle">
                  {t.city}
                </p>
              </div>

              <div className="flex items-center gap-1">
                {[...Array(t.rating)].map((_, j) => (
                  <Star
                    key={j}
                    className="w-4 h-4 fill-welljio-ink text-welljio-ink"
                    strokeWidth={1}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>
      {/* BLOG PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="blog-preview-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <SectionHeader
            eyebrow="Welljio Blog"
            title="Wellness, beautifully explained."
            className="mb-0"
          />
          <Link to="/blog" className="inline-flex items-center text-sm text-welljio-ink hover:text-welljio-accent transition-colors">
            All articles <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {posts.slice(0,3).map((p, i) => (
            <Link to={`/blog/${p.slug}`} key={p.slug} data-testid={`blog-card-${p.slug}`} className="welljio-premium-card overflow-hidden group" style={{ animationDelay: `${i*80}ms` }}>
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="welljio-card-image w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-[0.22em] text-welljio-subtle mb-2">{p.category} · {p.read_time}</p>
                <h3 className="font-heading text-xl font-semibold leading-snug mb-2 group-hover:text-welljio-accent transition-colors">{p.title}</h3>
                <p className="text-sm text-welljio-muted line-clamp-2">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
